/* CASH REPORT — one entity's report, the same for all three reports.

   Tuan's Set up sheet gives the Weekly Cash Report, the 13 Weeks Cash Plan and the Month-End
   Reconcile one flow, so they share this panel: where the report is in the flow (its stamps) and
   the one button that is the reader's move, the template to download and the upload, the Data
   Check, the files, the versions and the history. What is each report's own — its lines — comes in
   through `renderBody`, so the three screens differ in what they show and in nothing they do. */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Download, Upload, Send, CheckCircle2, AlertTriangle, Loader2, Paperclip, Trash2, MessageSquare, Undo2, FileText, X,
} from 'lucide-react';
import { DecisionDialog } from './DecisionDialog';
import { API, call, fmtStamp, readFile, MAX_FILE, StateChip } from './cashUi';

const FORWARD = { review: 'review', approve: 'approve', close: 'close' };
const TPL = { weekly: 'Template 1', plan: 'Template 2', monthend: 'Template 3' };
export const importLink = (type, period, entity) => `/cash/import?tab=${type}&period=${encodeURIComponent(period)}&entity=${encodeURIComponent(entity)}`;

/* THE INPUT AND THE REPORT ARE TWO SCREENS (Max, 2026-10-08: "1 module cho việc import nhé và module
   đó sẽ gồm 3 tab tương ứng với 3 template"). One panel, two modes, because both read the same
   report and its flow:
     mode="input"  — Cash Report Import, the Preparer's: the template three ways (typed here with
                     `Form`, downloaded as the import file, imported), the statements, and Send.
     mode="report" — the three report screens: what was sent and where it stands, to READ.
     mode="approve" — Approval, the Reviewer's: the one decision that is the reader's move
                     (review & agree, or send back), and nothing that
                     changes the figures. The decisions moved here from the report screens on
                     2026-10-15 (Max: "screen riêng ... chứ ko gộp vào màn hình của 3 cái report").
   Each mode points at the other when the reader's move is there, so nobody has to know which menu
   holds it. The Data Check, the version's lines, the files, the versions and the history are in both:
   the Preparer checks what was imported, the others decide on it, and it is the same version. */
/* WHAT A VERSION CHANGED against the one it replaced, under the line that says it was imported or corrected
   (Max, 2026-10-19: "có sửa nhưng lại không có chi tiết để là sửa cái gì"). The server works the list out —
   account, field, old → new — so a save that changed nothing says so instead of looking like an edit. */
const SHOW_FIRST = 8;
function VersionChanges({ ch, t }) {
  const [all, setAll] = useState(false);
  if (!ch) return null;
  if (!ch.items.length) return <div className="cw-evnote cw-changes none">{t('cashw.noChange', { v: ch.base })}</div>;
  const rows = all ? ch.items : ch.items.slice(0, SHOW_FIRST);
  return (
    <div className="cw-evnote cw-changes">
      <div className="cw-chhead">{t('cashw.changesAgainst', { v: ch.base, n: ch.total })}</div>
      <table className="cw-chtable">
        <tbody>
          {rows.map((it, i) => (
            <tr key={i}>
              <td className="cw-chwhere">{it.where}</td>
              <td className="cw-chfield">{it.field}</td>
              <td><span className="cw-chold">{it.old}</span></td>
              <td className="pa-muted">→</td>
              <td><span className="cw-chnew">{it.new}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      {ch.items.length > SHOW_FIRST && (
        <button type="button" className="cw-chmore" onClick={() => setAll((x) => !x)}>
          {all ? t('cashw.fewerChanges') : t('cashw.moreChanges', { n: ch.items.length - SHOW_FIRST })}
        </button>
      )}
      {ch.total > ch.items.length && <div className="pa-muted">{t('cashw.changesCut', { n: ch.total - ch.items.length })}</div>}
    </div>
  );
}

export default function CashReportPanel({
  t, type, entity, period, onChanged, heading, renderBody, Form, mode = 'report', fileKinds = ['statement', 'balance', 'other'],
}) {
  const input = mode === 'input';
  const approve = mode === 'approve';
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendNote, setSendNote] = useState('');
  const [comment, setComment] = useState('');
  const [fileKind, setFileKind] = useState(fileKinds[0]);
  const [fileAcct, setFileAcct] = useState('');
  const [deciding, setDeciding] = useState(null);   // { action: 'approve'|'reject', step }
  const [decisionNote, setDecisionNote] = useState('');
  /* THE FORM IS THE VIEW (Max, 2026-10-08: "sao lại có 2 cái khi import và khi edit vậy … import
     xong là tự động cho phép edit lại"). In Cash Report Import, whoever may enter a version sees the
     template as a form, filled with the version in the flow — an imported file lands in it, ready to
     be corrected and saved as the next version. There is no read-only table to open an editor from.
     `formKey` remounts the form whenever a new version arrives (import or save) or a change is
     discarded; an attachment or a comment reloads the panel without touching what is being typed. */
  const [form, setForm] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [dirty, setDirty] = useState(false);
  /* Cancel editing: the form goes back to the version in the flow (a remount), and Import is free
     again (Max, 2026-10-08: "thêm nút cancel cái đang edit á để import lại"). */
  const cancelEdit = () => { setDirty(false); setFormKey((k) => k + 1); };
  /* AND IT CANCELS THE IMPORT ITSELF (Max, 2026-10-09: "nút cancel đó sẽ cancel luôn cái đang
     import từ file á"). An imported file is a version the moment it lands, so dropping the typing
     alone left the import standing. When the server says the version in the flow is an import that
     can go (`d.discard` — an imported file, never one already sent), Cancel takes it out and the
     form reloads on the version before it, or empty; on a version typed here it only drops the
     typing, as before, so it cannot take a saved version out from under somebody. */
  const cancel = async () => {
    if (!d?.discard) { cancelEdit(); return; }
    const ok = await run(() => call(`${base}/discard`, { method: 'POST', body: JSON.stringify({ version: d.discard.version }) }),
      (j) => (j.back ? t('cashf.discardedBack', { v: j.version, back: j.back }) : t('cashf.discardedEmpty', { v: j.version })));
    if (ok) setDirty(false);
  };

  const base = `${API}/report/${type}/${encodeURIComponent(entity)}/${encodeURIComponent(period)}`;
  const load = useCallback(async () => {
    try { setD(await call(base)); setErr(''); } catch (e) { setErr(e.message); }
  }, [base]);
  useEffect(() => { setD(null); setMsg(null); load(); }, [load]);
  /* Only over a version that came in as its template — corrections, not typing from nothing (the server
     refuses an /entry with no version in the flow as well). */
  const formOn = input && !!Form && !!d && d.actions.includes('upload') && !!d.record?.version_no;
  const versionNo = d?.record?.version_no || 0;
  useEffect(() => {
    if (!formOn) { setForm(null); return undefined; }
    let live = true;
    call(`${base}/form`).then((f) => { if (live) { setForm(f); setDirty(false); setFormKey((k) => k + 1); } })
      .catch((e) => { if (live) setMsg({ text: e.message, bad: true }); });
    return () => { live = false; };
  }, [formOn, versionNo, base]);

  const say = (text, bad = false, extra) => setMsg({ text, bad, extra });
  const run = async (fn, okText) => {
    setBusy(true);
    try {
      const j = await fn();
      if (okText) say(typeof okText === 'function' ? okText(j) : okText);
      await load();
      onChanged();
      return true;
    } catch (e) { say(e.message, true, e.blocking); return false; } finally { setBusy(false); }
  };
  /* The version on this screen rides with the decision: the Preparer may replace the version while it is
     with the Reviewer, and the server refuses a decision on one the decider has not read. */
  const act = (action, note) => run(() => call(`${base}/action`, { method: 'POST', body: JSON.stringify({ action, note, version: d?.record?.version_no ?? null }) }),
    (j) => t(`cashw.done_${action}`) + (j.mailed === 'held' ? ` ${t('cashw.mailHeld')}` : ''));
  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_FILE) { say(t('cashw.tooBig'), true); return; }
    const b64 = await readFile(file);
    await run(() => call(`${base}/upload`, { method: 'POST', body: JSON.stringify({ fileBase64: b64, fileName: file.name }) }),
      (j) => (j.replaced ? t('cashw.replacedOk', { v: j.version, old: j.replaced }) : t('cashw.uploadedOk', { v: j.version })));
  };
  const attach = async (file, kind = fileKind, accountId = fileAcct || null) => {
    if (!file) return;
    if (file.size > MAX_FILE) { say(t('cashw.tooBig'), true); return; }
    const b64 = await readFile(file);
    await run(() => call(`${base}/file`, { method: 'POST', body: JSON.stringify({ fileBase64: b64, fileName: file.name, kind, accountId }) }),
      t('cashw.attached'));
  };
  /* The version the form opened on rides with the save, so a newer version (another tab, an import) is not
     quietly undone by an older screen. */
  const saveForm = (payload) => run(() => call(`${base}/entry`, { method: 'POST', body: JSON.stringify({ ...payload, baseVersion: d?.record?.version_no ?? null }) }),
    (j) => (j.replaced ? t('cashf.savedReplaced', { v: j.version, old: j.replaced }) : t('cashf.savedOk', { v: j.version })));

  if (err) return <div className="pa-section"><div className="pa-banner err">{err}</div></div>;
  if (!d) return <div className="pa-section"><div className="pa-empty"><Loader2 size={16} className="pa-spin" /></div></div>;

  const rep = d.record;
  const cur = d.current;
  const acts = new Set(d.actions);
  const forward = ['review', 'approve', 'close'].find((a) => acts.has(a));
  /* REOPEN — an agreed report sent back by its Reviewer for correction (Max, 2026-10-16: "có"). The
     server offers `return` on an agreed report to its Reviewer; it is the same send-back, so the same
     dialog and the same required reason — only the words say what it does to a report already done. */
  const reopen = acts.has('return') && ['reviewed', 'approved', 'closed'].includes(rep?.status);
  const canInput = acts.has('upload');
  const whyNot = d.inputBlocked || t('cashf.inputClosed');
  const row = { status: rep?.status || 'open', version: rep?.version_no || null };
  const errs = cur ? cur.checks.filter((c) => c.level === 'error') : [];
  const warns = cur ? cur.checks.filter((c) => c.level === 'warn') : [];
  const filesFor = (acct) => d.files.filter((f) => f.account_id === acct);
  const cancelled = new Set((d.versions || []).filter((v) => v.discarded_at).map((v) => v.version_no));
  const fileLink = (f) => `${API}/report-file/${f.file_pk}`;
  /* TWO STEPS — Max, 2026-10-16: "CFO và CEO thì ko tham gia vào quá trình duyệt nhé". The
     Reviewer's agreement is the last step. A report that went further under the old four-step
     flow still shows the stamps it carries; nothing is drawn for a step nobody will take. */
  const steps = [
    ['submitted', t('cashw.step_prep')], ['reviewed', t('cashw.step_review')],
    ...(rep?.approved_by ? [['approved', t('cashw.step_cfo')]] : []),
    ...(rep?.closed_by ? [['closed', t('cashw.step_ceo')]] : []),
  ];
  const closedLine = type === 'monthend'
    ? t('cashm.notEndedLine')
    : (rep?.status === 'returned' ? t('cashw.weekClosedReturned') : t('cashw.weekClosed'));

  /* THE FLOW, as its stamps: who did each step and when. One compact line — a step not yet taken
     is its name in grey, not a box saying "Not yet" (Max, 2026-10-08: "tùm lum và nhiều chữ"). */
  const stepLine = (
    <ol className="cw-steps">
      {steps.map(([k, label]) => {
        const by = rep?.[`${k}_by`]; const at = rep?.[`${k}_at`];
        return (
          <li key={k} className={by ? 'done' : ''} title={by ? undefined : t('cashw.notYet')}>
            <b>{label}</b>{by && <span>{by} · {fmtStamp(at)}</span>}
          </li>
        );
      })}
    </ol>
  );
  /* A report waiting on THIS reader's decision, seen anywhere but the approval screen — then one
     pointer says where the decision is taken. */
  const decideHere = !approve && !!(forward || (acts.has('return') && !reopen));

  return (
    <div className={`pa-section cw-entity${input ? ' cw-lean' : ''}`} id="cw-entity">
      {/* On a report screen the panel names its entity and period, because the table above lists
          many. In Submit the bar above already names both, so the head is the state and
          the steps — saying them twice was half of what made the screen read as clutter. */}
      {input ? (
        <div className="cw-enthead">
          <StateChip t={t} row={row} />
          {stepLine}
          {/* No link to the report here any more (Max, 2026-10-15: "ko cần … bị thừa"): the report
              is a menu item away, and on this screen it was a second door nobody needed. */}
        </div>
      ) : (
        <>
          <div className="cw-enthead">
            <h3>{d.entity.code} <span className="pa-muted">· {d.entity.name}</span></h3>
            <span className="pa-muted">{heading(d)}</span>
            <StateChip t={t} row={row} />
          </div>
          {stepLine}
        </>
      )}
      {rep?.status === 'returned' && (
        <div className="cs-returned cw-returned">
          {t('cashw.returnedBy', { by: rep.returned_by, at: fmtStamp(rep.returned_at) })} <b>{rep.returned_note}</b>
        </div>
      )}
      {!d.period.open && <div className="cs-boot">{closedLine}</div>}
      {!input && d.blocked && <div className="cs-boot">{t('cashw.selfBlocked', { step: t(`cashw.btn_${d.blocked}`) })}</div>}

      {/* THE INPUT — one toolbar: enter, import, the template, and Send once there is a version.
          The buttons are ALWAYS drawn and greyed out with the server's reason when the reader may
          not enter a version — hiding them left a screen called Import with no import on it. The
          reason rides on a wrapper because a disabled control shows no tooltip of its own. */}
      {input && (
        <div className="cf-input">
          {/* While the form holds unsaved changes, Import waits: a file would replace what is being
              typed without a word. Cancel editing sits beside it — the way back to importing. */}
          <span className="cf-wrap" data-tour="ci-import" title={!canInput ? whyNot : (dirty ? t('cashi.saveOrCancel') : undefined)}>
            <label className={`pa-btn primary${!canInput || busy || dirty ? ' disabled' : ''}`}>
              <Upload size={14} /> {rep?.version_no ? t('cashw.uploadAgain') : t('cashw.upload', { tpl: TPL[type] })}
              <input type="file" accept=".xlsx" hidden disabled={!canInput || busy || dirty} onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }} />
            </label>
          </span>
          {((formOn && dirty) || d.discard) && (
            <button type="button" className="pa-btn cf-cancel" disabled={busy} onClick={cancel}
              title={d.discard ? (d.discard.back
                ? t('cashf.cancelBack', { v: d.discard.version, back: d.discard.back })
                : t('cashf.cancelEmpty', { v: d.discard.version })) : undefined}>
              <X size={14} /> {d.discard ? t('cashf.cancelImport') : t('cashf.cancel')}
            </button>
          )}
          <a className="pa-btn" data-tour="ci-template" href={`${base}/template`}><Download size={14} /> {t('cashw.template', { tpl: TPL[type] })}</a>
          {acts.has('submit') && (
            <span className="cf-send">
              {/* A box to write in, not a one-line field (Max, 2026-10-09: "phần note này bị nhỏ quá"):
                  its own row under the buttons, several lines, as long as the server keeps (1,000). */}
              <textarea className="cs-note" rows={3} maxLength={1000} value={sendNote} aria-label={t('cashw.notePh')}
                onChange={(e) => setSendNote(e.target.value)} placeholder={t('cashw.notePh')} />
              <button type="button" className="pa-btn submit" disabled={busy || errs.length > 0}
                title={errs.length ? t('cashw.fixFirst') : undefined}
                onClick={() => act('submit', sendNote).then((ok) => ok && setSendNote(''))}>
                <Send size={14} /> {t('cashw.submit')}
              </button>
            </span>
          )}
          {!canInput && !decideHere && <span className="pa-muted cf-why">{whyNot}</span>}
          {/* With the Reviewer and still correctable (Max, 2026-10-19: "cho phép edit kể cả khi đang ở bước
              reviewer nha") — said, so a Preparer knows what an import does now. */}
          {canInput && rep?.status === 'submitted' && <span className="pa-muted cf-why" data-testid="cf-with-reviewer">{t('cashw.correctWhileReview', { v: rep.version_no })}</span>}
        </div>
      )}

      {/* On a report screen the Preparer's move — import, correct, send — is in the Import module;
          said where they are looking, with the way there. */}
      {!input && (acts.has('upload') || acts.has('submit')) && (
        <div className="cf-input cf-pointer">
          <span>{rep?.status === 'returned' ? t('cashi.pointerReturned') : t('cashi.pointer', { tpl: TPL[type] })}</span>
          <Link className="pa-btn primary" to={importLink(type, period, entity)}><Upload size={14} /> {t('cashi.goImport')}</Link>
        </div>
      )}

      {/* AND THE OTHER WAY ROUND (Max, 2026-10-09, playing the Reviewer on the Import screen: "hem
          biết làm gì nữa á"). The decisions — review, approve, close, send back — are the report
          screen's; when the report is waiting on THIS reader, the Import screen says so and points
          there instead of showing only that importing is the Preparer's. */}
      {/* The sentence alone, no button (Max, 2026-10-15: "bỏ cái Open Cash Report Approval nhé") —
          Cash Report Approval is a menu item of its own, and the line says which. */}
      {decideHere && (
        <div className="cf-input cf-pointer">
          <span>{t('cashi.pointerDecide')}</span>
        </div>
      )}

      {/* The decision row is the approval screen's, drawn only when it holds the reader's move. */}
      {approve && (forward || acts.has('return')) && (
        <div className="cw-acts">
          {forward && (
            <button type="button" className="pa-btn approve" disabled={busy}
              onClick={() => { setDecisionNote(''); setDeciding({ action: 'approve', step: forward }); }}>
              <CheckCircle2 size={14} /> {t(`cashw.btn_${forward}`)}
            </button>
          )}
          {acts.has('return') && !forward && (
            <button type="button" className="pa-btn cw-reopen" disabled={busy}
              onClick={() => { setDecisionNote(''); setDeciding({ action: 'reject', step: null }); }}>
              <Undo2 size={14} /> {t(reopen ? 'cashw.reopen' : 'cashw.sendBack')}
            </button>
          )}
          {reopen && <span className="cw-reopen-why">{t('cashw.reopenWhy')}</span>}
        </div>
      )}

      {msg && (
        <div className={`pa-banner ${msg.bad ? 'err' : 'ok'}`}>
          {msg.text}
          {msg.extra && <ul className="cs-blocking">{msg.extra.slice(0, 20).map((x) => <li key={x}>{x}</li>)}</ul>}
        </div>
      )}

      {!cur && !formOn && <div className="pa-empty">{input ? t('cashw.nothingUploaded', { tpl: TPL[type] }) : t('cashi.nothingYet', { tpl: TPL[type] })}</div>}
      {cur && (
        <>
          {/* THE DATA CHECK — an error stops the report being sent, a warning is for the reviewer.
              One line per problem (the server folds them); a clean check is one green line. */}
          <div className="cs-checkbar">
            {errs.length > 0 && <span className="cs-sum error"><AlertTriangle size={14} /> {t('cashw.sumErrors', { count: errs.length })}</span>}
            {warns.length > 0 && <span className="cs-sum warn">{t('cashw.sumWarns', { count: warns.length })}</span>}
            {!errs.length && !warns.length && <span className="cs-sum ok"><CheckCircle2 size={14} /> {t('cashi.checkPassed')}</span>}
          </div>
          {(errs.length > 0 || warns.length > 0) && (
            <ul className="cw-checks">
              {[...errs, ...warns].map((c, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={i} className={c.level}>{c.level === 'error' ? <AlertTriangle size={13} /> : <span className="cw-dot" />} {c.msg}</li>
              ))}
            </ul>
          )}
          {!formOn && <div data-tour="report-body">{renderBody(d, { filesFor, fileLink })}</div>}
        </>
      )}
      {formOn && form && (
        <Form key={formKey} t={t} form={form} busy={busy} onSave={saveForm}
          onCancel={cancelEdit} onAttach={(f, k, a) => attach(f, k, a)} onDirty={setDirty} filesFor={filesFor} fileLink={fileLink}
          entity={entity} preparedAt={(d.versions || []).find((v) => v.version_no === rep?.version_no)?.uploaded_at || null} />
      )}

      {/* STATEMENTS AND SCREENSHOTS — Tuan's buttons per account. */}
      <h4 className="cw-h" data-tour="ci-files">{t('cashw.filesTitle')}</h4>
      {input && acts.has('file') && (
        <div className="cw-attach">
          <select value={fileKind} onChange={(e) => setFileKind(e.target.value)}>
            {fileKinds.map((k) => <option key={k} value={k}>{t(`cashw.kind_${k}`)}</option>)}
          </select>
          <select value={fileAcct} onChange={(e) => setFileAcct(e.target.value)}>
            <option value="">{t('cashw.wholeEntity')}</option>
            {d.accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_id} · {a.currency} · {a.bank_name}</option>)}
          </select>
          <label className={`pa-btn${busy ? ' disabled' : ''}`}>
            <Paperclip size={14} /> {t('cashw.attach')}
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx" hidden disabled={busy} onChange={(e) => { attach(e.target.files[0]); e.target.value = ''; }} />
          </label>
        </div>
      )}
      {d.files.length ? (
        <ul className="cw-files">
          {d.files.map((f) => (
            <li key={f.file_pk}>
              <a href={fileLink(f)} target="_blank" rel="noreferrer"><FileText size={13} /> {f.original_name}</a>
              <span className="pa-muted"> · {t(`cashw.kind_${f.kind}`)}{f.account_id ? ` · ${f.account_id}` : ''} · {f.uploaded_by} · {fmtStamp(f.uploaded_at)}</span>
              {input && acts.has('file') && (
                <button type="button" className="cw-icon" title={t('cashw.removeFile')} disabled={busy}
                  onClick={() => run(() => call(fileLink(f), { method: 'DELETE' }))}><Trash2 size={13} /></button>
              )}
            </li>
          ))}
        </ul>
      ) : <div className="pa-muted cw-none">{t('cashw.noFiles')}</div>}

      {/* No separate list of versions: the history below already says who imported or entered each
          one and when, with the file's name — two lists of the same events is the clutter. */}

      {/* THE HISTORY — Young asked for every send, comment and edit; the report row only says where
          it stands now. */}
      <h4 className="cw-h" data-tour="ci-history">{t('cashw.historyTitle')}</h4>
      <div className="cw-comment">
        <input className="cs-note" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t('cashw.commentPh')} />
        <button type="button" className="pa-btn" disabled={busy || !comment.trim()}
          onClick={() => run(() => call(`${base}/comment`, { method: 'POST', body: JSON.stringify({ note: comment }) })).then((ok) => ok && setComment(''))}>
          <MessageSquare size={14} /> {t('cashw.comment')}
        </button>
      </div>
      {d.events.length ? (
        <ul className="cw-events">
          {d.events.map((ev) => (
            <li key={ev.event_pk} className={`ev-${ev.action}`}>
              <span className="cw-evwhen">{fmtStamp(ev.at)}</span>
              <span>
                <b>{ev.by_name}</b>{ev.by_role ? <span className="pa-muted"> ({ev.by_role})</span> : ''} {t(`cashw.ev_${ev.action}`, { v: ev.version_no })}
                {['upload', 'discard'].includes(ev.action) && ev.note && <span className="pa-muted"> · {ev.note}</span>}
                {['upload', 'entry'].includes(ev.action) && ev.version_no === rep?.version_no && <span className="cw-tag">{t('cashw.current')}</span>}
                {['upload', 'entry'].includes(ev.action) && cancelled.has(ev.version_no) && <span className="cw-tag off">{t('cashw.cancelled')}</span>}
              </span>
              {ev.note && !['upload', 'discard'].includes(ev.action) && <div className="cw-evnote">{ev.note}</div>}
              {['upload', 'entry'].includes(ev.action) && <VersionChanges ch={d.changes?.[ev.version_no]} t={t} />}
            </li>
          ))}
        </ul>
      ) : <div className="pa-muted cw-none">{t('cashw.noEvents')}</div>}

      {deciding && (
        <DecisionDialog
          spec={{ action: deciding.action, group: { groupId: `${entity} · ${period}`, groupName: d.report } }}
          note={decisionNote}
          onNote={setDecisionNote}
          busy={busy}
          selectedIds={[]}
          /* Send back alone (the reader is barred from the forward step) has nothing to agree to,
             so the dialog's Agree side is not taken. */
          onSwitch={(a) => setDeciding((x) => (x.step || a === 'reject' ? { ...x, action: a } : x))}
          onCancel={() => { setDeciding(null); setDecisionNote(''); }}
          onConfirm={async () => {
            const action = deciding.action === 'reject' ? 'return' : FORWARD[deciding.step];
            const ok = await act(action, decisionNote);
            /* A refused decision closes the dialog too, or its overlay hides the banner saying why. */
            setDeciding(null);
            if (ok) setDecisionNote('');
          }}
        />
      )}
    </div>
  );
}
